import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService, CommonApiResponse } from '@techextensor/tab-core-utility';
import { TabSdk } from '@techextensor/tab-sdk';
import { ExternalAppsApi } from '../const/constants';

/**
 * What an administrator may write into an external app's address to have the launcher fill in who is
 * opening it. Nothing here is specific to any one app: it is the generic way to hand an outside app a
 * sign-in hint, whichever identity provider that app uses.
 *
 * The common case is an app that redirects to Microsoft. With several Microsoft accounts signed in to
 * one browser, Entra shows an account picker; `login_hint` removes it. So an administrator stores
 *
 *     https://<app>/sso/start?login_hint={email}
 *
 * and each person's own address is substituted when their tile is opened. Without a placeholder,
 * nothing about the person is sent — this is opt-in per app, one address at a time.
 */
const LAUNCH_PLACEHOLDERS: ReadonlyArray<{ token: string; value: () => string }> = [
  { token: 'email', value: () => currentUser().email },
  { token: 'upn', value: () => currentUser().upn },
  { token: 'userid', value: () => currentUser().userId }
];

/**
 * Both spellings, because the server normalises what it stores: `Uri.AbsoluteUri` percent-encodes the
 * braces, so an address typed as `{email}` comes back as `%7Bemail%7D`. Matching both means this works
 * against the Identity service as deployed, with no release on that side.
 */
const PLACEHOLDER_PATTERN = /\{([a-z]+)\}|%7B([a-z]+)%7D/gi;

/** The signed-in person, read through the SDK facade rather than storage directly. */
function currentUser(): { email: string; upn: string; userId: string } {
  const info: any = TabSdk.store?.getUserInfo?.() ?? {};
  const attributes = info?.user?.Attributes ?? {};
  return {
    email: attributes.Email ?? '',
    upn: attributes.UserName ?? info?.user?.UserName ?? '',
    userId: TabSdk.store?.userId ?? info?.user?.Id ?? ''
  };
}

/** One external app tile in a person's launcher. */
export interface ExternalAppTile {
  appId: string;
  /** What the tile says. The app code is generated and never shown. */
  name: string;
  /** Where the tile opens — always in a new tab, never through the TAF session hand-off. */
  url: string;
  iconUrl?: string | null;
  /** The host the browser will actually resolve, in punycode where it differs from what was typed. */
  host?: string | null;
  /** True when the host is a punycode name, i.e. possibly a lookalike. Worth showing differently. */
  isPunycodeHost: boolean;
}

/** One external app as a tenant administrator sees it. */
export interface ExternalAppAdminRow extends ExternalAppTile {
  appCode: string;
  description?: string | null;
  /** `entra` when synced from the tenant's Microsoft directory, `manual` when added by hand. */
  source: string;
  externalKey?: string | null;
  assignedUserCount: number;
  isActive: boolean;
  /** Set when the stored address no longer passes the server's policy, with the reason. */
  addressProblem?: string | null;
}

/**
 * The apps a tenant offers that are not built in TAF.
 *
 * Read from the Identity service rather than the app-switcher DSQ: that query is registered in the
 * baseline database, not in this repo, so it cannot be taught the new columns from here. Every call
 * goes through core-utility's `ApiService`, so the auth interceptor attaches the token and the
 * response comes back normalised (`IsSuccess` / `Result` / `Errors`).
 *
 * The tenant is passed in the path, not left to the `TenantId` header, because the launcher's tenant
 * rail lets someone look at any tenant they belong to while the interceptor overwrites that header
 * with the tenant they are currently working in. The server checks membership itself.
 */
@Injectable({ providedIn: 'root' })
export class ExternalAppsService {
  private readonly _api = inject(ApiService);

  /**
   * This person's external tiles for one tenant. Never throws: the launcher must still render its TAF
   * apps if this call fails, so a failure is an empty list and a console warning.
   */
  async listForUser(tenantId: string): Promise<ExternalAppTile[]> {
    try {
      const response = await firstValueFrom(
        this._api.getFromService('identityService', ExternalAppsApi.forUser(tenantId))
      );
      const tiles = (this.unwrap(response) as ExternalAppTile[]) ?? [];
      // Resolved once, here, so the anchor's href and the click handler cannot disagree about where
      // the tile goes. The administration list deliberately does NOT do this: an administrator must
      // see the address they typed, placeholder and all.
      return tiles.map(tile => ({ ...tile, url: this.resolveLaunchUrl(tile.url) }));
    } catch (error) {
      console.warn('[ExternalApps] could not load external apps for this tenant', error);
      return [];
    }
  }

  /** Every external app in the tenant, for the administration screen. Throws so the page can show why. */
  async listForTenant(tenantId: string): Promise<ExternalAppAdminRow[]> {
    const response = await firstValueFrom(
      this._api.getFromService('identityService', ExternalAppsApi.forTenant(tenantId))
    );
    return (this.unwrap(response) as ExternalAppAdminRow[]) ?? [];
  }

  async create(
    tenantId: string,
    app: {
      name: string;
      url: string;
      description?: string | null;
      /** The tile's picture, as an https address. Without one the launcher renders the default tile. */
      iconUrl?: string | null;
      assignedUserIds?: string[];
    }
  ): Promise<string> {
    const response = await firstValueFrom(
      this._api.postToService('identityService', ExternalAppsApi.create(tenantId), app)
    );
    return this.unwrap(response) as string;
  }

  async update(
    tenantId: string,
    appId: string,
    /** Sending no icon clears the one that was there: an update replaces the whole record. */
    app: { name: string; url: string; description?: string | null; iconUrl?: string | null }
  ): Promise<void> {
    const response = await firstValueFrom(
      this._api.putToService('identityService', ExternalAppsApi.item(tenantId, appId), app)
    );
    this.unwrap(response);
  }

  /** Takes the app out of everyone's launcher. The catalogue row stays, for audit. */
  async remove(tenantId: string, appId: string): Promise<void> {
    const response = await firstValueFrom(
      this._api.deleteFromService('identityService', ExternalAppsApi.item(tenantId, appId), null)
    );
    this.unwrap(response);
  }

  /**
   * Opens an external app. A new tab every time, with the opener severed and the referrer suppressed:
   * TAF's own URLs carry tenant, app and screen identifiers, and navigating out of TAF in the same tab
   * looks identical to a TAF page while being somebody else's.
   *
   * Nothing here touches the session hand-off the TAF app tiles use.
   */
  open(tile: ExternalAppTile): void {
    window.open(tile.url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Fills in the placeholders an administrator wrote into an address, with the values of whoever is
   * opening the tile. Percent-encoded, because the value lands in a query string and an address can
   * contain characters that would otherwise end the parameter.
   *
   * A placeholder nobody recognises is left exactly as typed rather than blanked, so a mis-spelled
   * token shows up as itself instead of vanishing. A recognised one with nothing behind it resolves
   * to empty, which every identity provider treats as "no hint given".
   */
  private resolveLaunchUrl(url: string): string {
    if (!url || !PLACEHOLDER_PATTERN.test(url)) {
      PLACEHOLDER_PATTERN.lastIndex = 0;
      return url;
    }
    PLACEHOLDER_PATTERN.lastIndex = 0;

    return url.replace(PLACEHOLDER_PATTERN, (whole, braced?: string, encoded?: string) => {
      const token = (braced ?? encoded ?? '').toLowerCase();
      const placeholder = LAUNCH_PLACEHOLDERS.find(p => p.token === token);
      if (!placeholder) {
        console.warn(`[ExternalApps] unknown placeholder "${whole}" in an external app address`);
        return whole;
      }
      return encodeURIComponent(placeholder.value());
    });
  }

  private unwrap(response: CommonApiResponse): unknown {
    if (!response?.IsSuccess) {
      throw new Error(response?.Errors?.[0] ?? 'The request was not completed.');
    }
    return response.Result;
  }
}
